/**
 * Pure logic that turns plant-level procurement + invoice records into
 * dashboard figures. No React, no fetching (see procurementApi.js).
 *
 * Conventions follow the app's own 5D / Schedule4D code:
 *  - a PO line's `cost` is a UNIT cost; line cost = cost x procured qty
 *    (Schedule4D getTotalCost, PMSTab2/5d Cost.jsx), first positive cost per
 *    MRQ line wins so a PO revision isn't counted twice;
 *  - an MRQ line belongs to a WBS code through its own `wbs` ("V201.25 (Shell)"
 *    or "2.1 (Shell)"), falling back to the MRQ header's ("2.2.1-MRQ-Struct").
 *
 * Two code spellings match a line (see procurementAliases): the tag-prefixed
 * one is specific to that vessel; the plain one is plant-wide, because MRQ lines
 * carry no project reference. The result says how much matched each way.
 */

export const INVOICE_DISCIPLINES = ["engineering", "procurement", "fabrication"];

// A discipline's invoices are its inv1, inv2, ... keys ({ date, amount } each).
const INVOICE_KEY = /^inv\d+$/;

const num = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

// What may follow a matched code: nothing, a separator ("2.1 (Shell)",
// "2.1-MRQ", "2.1::UID") or a child index ("2.2.1" under "2.2"). Never a digit,
// so "2.10" is not "2.1".
const BOUNDARY = /^(?:$|[\s(:-]|\.\d)/;

/**
 * The line a WBS string belongs to, or null. `aliases` is
 * procurementAliases()'s Map (lower-cased spelling -> { code, scoped }); the
 * longest spelling that fits wins. Line wbs first, then the MRQ header's - and a
 * line wbs that names nothing we know ("Procurement") does not fall back.
 */
export const resolveWbsCode = (lineWbs, headerWbs, aliases) => {
  const source = (
    String(lineWbs ?? "").trim() || String(headerWbs ?? "").trim()
  ).toLowerCase();
  if (!source) return null;

  let best = null;
  aliases.forEach((info, alias) => {
    if (!source.startsWith(alias)) return;
    if (!BOUNDARY.test(source.slice(alias.length))) return;
    if (!best || alias.length > best.alias.length) best = { alias, ...info };
  });

  return best ? { code: best.code, scoped: best.scoped } : null;
};

/**
 * @param mrqs         [{ id, wbs, mrqMaterials: [{ id, wbs, procuredQty, quantity }] }]
 * @param posByMrqId   { [mrqId]: [{ id, poMaterials: [{ mrqMaterialId, cost }] }] }
 * @param payments     [{ amount, poId | po: { id } }]
 * @param paymentsLoaded false when the payments call failed, so Paid stays "no
 *                     data" instead of a false $0
 * @param aliases      procurementAliases() of the dashboard lines
 * @returns { byCode: Map<code, { ac, pc }>, events: { act, pd } (dated amounts
 *            for the S-curve), unlinkedCost, scopedCost, plantWideCost, counts }
 */
export const linkProcurement = ({
  mrqs,
  posByMrqId,
  payments,
  paymentsLoaded = true,
  aliases,
}) => {
  const entries = [];
  const seenMrqMaterial = new Set();
  let poCount = 0;

  (Array.isArray(mrqs) ? mrqs : []).forEach((mrq) => {
    const mrqMaterialById = new Map(
      (mrq?.mrqMaterials || []).map((mm) => [String(mm.id), mm]),
    );

    (posByMrqId?.[mrq.id] || []).forEach((po) => {
      poCount += 1;
      (po?.poMaterials || []).forEach((pm) => {
        const mrqMaterialId = pm.mrqMaterialId ?? pm.mrqMaterial?.id;
        if (mrqMaterialId === null || mrqMaterialId === undefined) return;
        const key = String(mrqMaterialId);
        if (seenMrqMaterial.has(key)) return;

        const mm = mrqMaterialById.get(key) || pm.mrqMaterial || {};
        const unitCost = num(pm.cost);
        const qty = num(mm.procuredQty) || num(mm.quantity) || 0;
        if (!(unitCost > 0 && qty > 0)) return;

        seenMrqMaterial.add(key);
        const hit = resolveWbsCode(mm.wbs, mrq.wbs, aliases);
        entries.push({
          poId: po.id,
          date: po.poDate ?? po.createdAt ?? null,
          code: hit?.code ?? null,
          scoped: Boolean(hit?.scoped),
          cost: unitCost * qty,
        });
      });
    });
  });

  // A payment belongs to a PO, not to a WBS line, so it is spread over that
  // PO's lines by their share of the PO's cost.
  const paidByPo = new Map();
  const paymentList = [];
  (Array.isArray(payments) ? payments : []).forEach((payment) => {
    const poId = payment?.po?.id ?? payment?.poId;
    const amount = num(payment?.amount);
    if (poId === null || poId === undefined || amount === null) return;
    paidByPo.set(String(poId), (paidByPo.get(String(poId)) || 0) + amount);
    paymentList.push({
      poId: String(poId),
      amount,
      date: payment.paymentDate ?? payment.payment_date ?? payment.createdAt ?? null,
    });
  });

  const costByPo = new Map();
  entries.forEach((entry) => {
    const key = String(entry.poId);
    costByPo.set(key, (costByPo.get(key) || 0) + entry.cost);
  });

  const linkedCostByPo = new Map();
  const byCode = new Map();
  let unlinkedCost = 0;
  let scopedCost = 0;
  let plantWideCost = 0;

  entries.forEach((entry) => {
    if (!entry.code) {
      unlinkedCost += entry.cost;
      return;
    }
    if (entry.scoped) scopedCost += entry.cost;
    else plantWideCost += entry.cost;
    linkedCostByPo.set(
      String(entry.poId),
      (linkedCostByPo.get(String(entry.poId)) || 0) + entry.cost,
    );

    const key = String(entry.poId);
    const paid = paidByPo.get(key) || 0;
    const share = costByPo.get(key) > 0 ? (paid * entry.cost) / costByPo.get(key) : 0;

    const bucket = byCode.get(entry.code) || { ac: 0, pc: paymentsLoaded ? 0 : null };
    bucket.ac += entry.cost;
    if (paymentsLoaded) bucket.pc += share;
    byCode.set(entry.code, bucket);
  });

  // Dated amounts for the S-curve. Actual is each linked PO line at its PO
  // date; Paid is each payment at its date, scaled to the share of its PO that
  // is linked - the same split that produced the per-line Paid above.
  const events = { act: [], pd: [] };
  entries.forEach((entry) => {
    if (entry.code) events.act.push({ date: entry.date, amount: entry.cost });
  });
  if (paymentsLoaded) {
    paymentList.forEach((payment) => {
      const total = costByPo.get(payment.poId);
      const linkedCost = linkedCostByPo.get(payment.poId);
      if (!(total > 0) || !linkedCost) return;
      events.pd.push({
        date: payment.date,
        amount: (payment.amount * linkedCost) / total,
      });
    });
  }

  return {
    byCode,
    events,
    unlinkedCost,
    scopedCost,
    plantWideCost,
    counts: {
      mrqs: Array.isArray(mrqs) ? mrqs.length : 0,
      pos: poCount,
      payments: Array.isArray(payments) ? payments.length : 0,
    },
  };
};

/**
 * Plant MIS JSON -> { engineering|procurement|fabrication: { progress, amount } }.
 * `amount` is the sum of that discipline's invN.amount entries, null when none
 * were entered. Same shape MISContext / InvoicesTab read and write
 * (`data.data.invoices`), tolerating a couple of unwrap levels.
 */
export const extractInvoices = (mis) => {
  const invoices = mis?.data?.data?.invoices ?? mis?.data?.invoices ?? mis?.invoices;
  if (!invoices || typeof invoices !== "object") return {};

  const out = {};
  INVOICE_DISCIPLINES.forEach((discipline) => {
    const row = invoices[discipline];
    if (!row || typeof row !== "object") return;

    let amount = null;
    Object.entries(row).forEach(([key, value]) => {
      if (!INVOICE_KEY.test(key)) return;
      const n = num(value?.amount);
      if (n !== null) amount = (amount || 0) + n;
    });

    out[discipline] = { progress: num(row.progress), amount };
  });
  return out;
};

/**
 * Every dated invoice in the plant MIS (all three disciplines - the same set
 * the Invoiced total is built from), for the S-curve.
 */
export const extractInvoiceEvents = (mis) => {
  const invoices = mis?.data?.data?.invoices ?? mis?.data?.invoices ?? mis?.invoices;
  if (!invoices || typeof invoices !== "object") return [];

  const events = [];
  INVOICE_DISCIPLINES.forEach((discipline) => {
    Object.entries(invoices[discipline] || {}).forEach(([key, value]) => {
      if (!INVOICE_KEY.test(key)) return;
      const amount = num(value?.amount);
      if (amount !== null) events.push({ date: value?.date || null, amount });
    });
  });
  return events;
};
